/* globals $ */
import { registerClass } from 'libs/ComponentLoader'
import {
  handleize,
  parseGroupValue
} from '_global/js/helpers'

import {
  search,
  SEARCH_RESULTS
} from 'libs/rcmSearchApi'

import './styles.scss'

class SidebarFilters {
  constructor (element) {
    this.$el = $(element)

    this.filters = []
    this.filterCheckboxes = [] // cache what we already have, to avoid jquery lookups with every request
    this.needApply = false
    this.$originalFilters = null

    this.$activeFilterList = this.$el.find('.sidebarFilters-activeFilterList')
    this.$clearFiltersButton = this.$el.find('.sidebarFilters-clearFiltersButton')
    this.$activeFilterTemplate = $(this.$el.find('.sidebarFilters-activeFilterTemplate').html())
    this.$filterCheckboxTemplate = $(this.$el.find('.sidebarFilters-filterCheckboxTemplate').html())

    this.updateFilters = this.updateFilters.bind(this)
    this.parseFilters = this.parseFilters.bind(this)
    this.removeFilter = this.removeFilter.bind(this)
    this.clearAllFilters = this.clearAllFilters.bind(this)
    this.waitForApply = this.waitForApply.bind(this)
    this.applyFilters = this.applyFilters.bind(this)
    this.cancelFilters = this.cancelFilters.bind(this)

    this.$el.on('change', '[type="checkbox"]', this.parseFilters)
    this.$activeFilterList.on('click', '.activeFilterButton', this.removeFilter)
    this.$clearFiltersButton.on('click', this.clearAllFilters)

    this.accordionGroups = []
    this.$el.find('.sidebarFilters-filterAccordion').each((index, element) => {
      const $element = $(element)
      if ($element[0].hasAttribute('data-group')) {
        this.accordionGroups[$element.attr('data-group')] = {
          $element: $element,
          $filterList: $element.find('.sidebarFilters-filterList')
        }
      }
    })

    search.on(SEARCH_RESULTS, this.updateFilters)
  }

  updateFilters (data) {
    let activeFilters = []

    // show all available filters
    if (data && data.facets) {
      for (let i = 0, len = data.facets.length; i < len; i++) {
        if (data.facets[i].attribute === 'tags') {
          data.facets[i].buckets.forEach((bucket) => {
            const {
              value: tag,
              selected
            } = bucket
            const tagParts = tag.split(':')
            if (tagParts.length < 2) {
              return
            }
            const { group: tagGroup, value: tagName } = parseGroupValue(tag)
            const tagGroupLower = tagGroup.toLowerCase()
            const filterId = handleize(tag)

            this.processFilter({
              id: filterId,
              text: tagName,
              value: tag,
              facet: 'tags',
              accordionGroup: tagGroupLower,
              bucket: bucket
            })

            if (selected) {
              activeFilters.push({
                text: tagName,
                id: filterId
              })
            }
          })
        } else if (data.facets[i].attribute === 'product_type') {
          if (!this.accordionGroups['product_type']) {
            continue
          }
          data.facets[i].buckets.forEach((bucket) => {
            const {
              value: productTypeName,
              selected
            } = bucket

            const filterId = `product_type-${handleize(productTypeName)}`

            this.processFilter({
              id: filterId,
              text: productTypeName,
              value: productTypeName,
              facet: 'product_type',
              accordionGroup: 'product_type',
              bucket: bucket
            })

            if (selected) {
              activeFilters.push({
                text: productTypeName,
                id: filterId
              })
            }
          })
        }
      }
    }

    this.$activeFilterList.html('')
    this.$activeFilterList.addClass('u--hide')
    this.$clearFiltersButton.addClass('u--hide')

    if (activeFilters.length) {
      let activeFilterItems = []
      activeFilters.forEach((filter) => {
        const {
          text,
          id
        } = filter
        const $activeFilter = this.$activeFilterTemplate.clone()
        $activeFilter.find('.activeFilterButton-text').text(text)
        $activeFilter.find('.activeFilterButton').attr('data-filter', id)

        activeFilterItems.push($activeFilter)
      })
      this.$activeFilterList.append(activeFilterItems)

      this.$clearFiltersButton.removeClass('u--hide')
      this.$activeFilterList.removeClass('u--hide')
    }

    // expand / collapse filter groups
    for (let group in this.accordionGroups) {
      const $groupElement = this.accordionGroups[group].$element
      const $groupCheckboxes = $groupElement.find('[type="checkbox"]')
      const $accordionTrigger = $groupElement.find('.accordion__trigger')
      if ($groupCheckboxes.filter(':checked').length) {
        if ($accordionTrigger.is('[aria-expanded="false"]')) {
          $accordionTrigger.click()
        }
      } else {
        if ($accordionTrigger.is('[aria-expanded="true"]')) {
          $accordionTrigger.click()
        }
      }
    }

    // custom stuff for ensuring a11y accordion is properly keyboard tabbable, see libs/a11yAccordionTabIndex.js
    $(document).trigger('a11y:refresh_accordion', [this.$el])
  }

  processFilter (data) {
    const {
      id,
      text,
      value,
      facet,
      accordionGroup,
      bucket
    } = data

    const {
      count,
      selected
    } = bucket

    if (typeof this.filterCheckboxes[id] === 'undefined') {
      const $checkbox = this.$filterCheckboxTemplate.clone()
      $checkbox.find('input')
        .attr('id', id)
        .attr('name', id)
        .attr('data-facet', facet)
        .attr('value', value)
      $checkbox.find('label')
        .attr('for', id)
        .text(text)

      if (this.accordionGroups[accordionGroup]) {
        this.accordionGroups[accordionGroup].$filterList.append($checkbox)
        this.accordionGroups[accordionGroup].$element.removeClass('u--hide')

        // custom sorting for the level filter
        // kinda messy... could use a bit of clean up
        if (accordionGroup === 'level') {
          // move arct to the bottom
          let $li = this.accordionGroups[accordionGroup].$filterList.find('[name="level-arct"]').closest('li')
          if ($li.length) {
            this.accordionGroups[accordionGroup].$filterList.append($li)
          }
          // move preperatory to the top
          $li = this.accordionGroups[accordionGroup].$filterList.find('[name="level-preparatory"]').closest('li')
          if ($li.length) {
            this.accordionGroups[accordionGroup].$filterList.prepend($li)
          }
        }
      }
      this.filterCheckboxes[id] = $checkbox
    }

    this.filterCheckboxes[id].attr('data-count', count)
    this.filterCheckboxes[id].find('input').prop('checked', (selected === 'true'))
  }

  parseFilters () {
    if (this.needApply) {
      return
    }
    let filters = {
      tags: [],
      product_type: []
    }

    this.$el.find('[type="checkbox"]:checked').each((index, element) => {
      const $element = $(element)
      const facet = $element.attr('data-facet')
      if (filters[facet]) {
        filters[facet].push($element.val())
      }
    })

    search.setFilters(filters)
  }

  removeFilter (e) {
    const $button = $(e.currentTarget)
    const filterId = $button.attr('data-filter')
    $button.remove()

    this.$el.find(`#${filterId}`).prop('checked', false)
    this.parseFilters()
  }

  clearAllFilters () {
    this.$el.find('.sidebarFilters-activeFilterButton').remove()
    this.$el.find('[type="checkbox"]').prop('checked', false)
    this.needApply = false
    this.parseFilters()
  }

  waitForApply () {
    this.needApply = true
    this.$originalFilters = this.$el.find('[type="checkbox"]:checked')
  }

  applyFilters () {
    this.needApply = false
    this.parseFilters()
  }

  cancelFilters () {
    this.$el.find('[type="checkbox"]:checked').prop('checked', false)
    this.$originalFilters.prop('checked', true)
    this.needApply = false
    this.$originalFilters = null
  }
}

registerClass('SidebarFilters', SidebarFilters)
